import { useEffect, useRef, useState } from 'react';
import {
  subscribeStorageFreed,
  subscribeUploadProgress,
  UploadProgressEvent,
} from 'helpers/upload-progress-bus';
import { hasStoragePlan } from 'helpers/storage';

// The vault's storage use, kept live with the app's uploads (so the readout
// stays right while the menu is open during an upload): bytes currently
// mid-upload, plus bytes from uploads that finished but aren't yet reflected
// in vault.totalStorage (until the vault is refetched).
export const useStorageUsage = (vault: VaultType) => {
  const inFlightRef = useRef<Map<string, number>>(new Map());
  const [settledBytes, setSettledBytes] = useState(0);
  const [inFlightBytes, setInFlightBytes] = useState(0);

  // A refreshed vault (new totalStorage) supersedes our local deltas.
  useEffect(() => {
    inFlightRef.current.clear();
    setSettledBytes(0);
    setInFlightBytes(0);
  }, [vault.totalStorage, vault.id]);

  useEffect(
    () =>
      subscribeUploadProgress((event: UploadProgressEvent) => {
        const inFlight = inFlightRef.current;
        if (event.done) {
          inFlight.delete(event.uploadId);
          // Keep successful bytes counted until the vault refetch lands.
          if (event.loadedBytes > 0) {
            setSettledBytes((prev) => prev + event.loadedBytes);
          }
        } else {
          inFlight.set(event.uploadId, event.loadedBytes);
        }
        setInFlightBytes(
          [...inFlight.values()].reduce((sum, loaded) => sum + loaded, 0),
        );
      }),
    [],
  );

  // A deleted memory returns its bytes to the vault.
  useEffect(
    () => subscribeStorageFreed((freed) => setSettledBytes((p) => p - freed)),
    [],
  );

  if (!hasStoragePlan(vault)) return null;

  const usedBytes = Math.max(
    Math.min(
      vault.totalStorage + settledBytes + inFlightBytes,
      vault.storageLimit,
    ),
    0,
  );
  const percentUsed = Math.min(
    Math.round((usedBytes / vault.storageLimit) * 100),
    100,
  );
  return { usedBytes, percentUsed };
};
